import { asOfLabel, DASH, relativeDay } from './dates';

import type { ActivationMuseum, FirstSource, MuseumStatus } from '../schemas/activation';

/**
 * Everything the list screen decides, kept out of the component so it can be
 * tested without a DOM (the web app has none — see `vitest.config.ts`).
 */

/** A museum is its name; a tenant the catalog does not know is still a row, so it shows its id. */
export function museumLabel(museum: { tenantId: string; name: string | null }): string {
    return museum.name ?? museum.tenantId;
}

export const STATUS_LABEL: Record<MuseumStatus, string> = {
    not_started: 'Not started',
    in_progress: 'In progress',
    completed: 'Completed',
};

export const ORIGIN_LABEL: Record<FirstSource, string> = {
    invite: 'From invitation',
    tab: 'Came in directly',
};

/* ------------------------------------------------------------------ tiles - */

export interface ActivationCounts {
    readonly total: number;
    readonly notStarted: number;
    /** The part of `notStarted` that never opened the page at all. */
    readonly neverOpened: number;
    readonly inProgress: number;
    readonly completed: number;
}

/** The four tiles. Counted over every museum, never over what the filters left. */
export function countMuseums(museums: readonly ActivationMuseum[]): ActivationCounts {
    let notStarted = 0;
    let neverOpened = 0;
    let inProgress = 0;
    let completed = 0;

    for (const museum of museums) {
        if (museum.status === 'not_started') {
            notStarted += 1;
            if (!museum.opened) neverOpened += 1;
        } else if (museum.status === 'in_progress') {
            inProgress += 1;
        } else {
            completed += 1;
        }
    }

    return { total: museums.length, notStarted, neverOpened, inProgress, completed };
}

export function neverOpenedHint(neverOpened: number): string {
    return `${String(neverOpened)} never opened the page`;
}

/* ---------------------------------------------------------------- filters - */

export type StatusFilter = 'all' | MuseumStatus;
export type OriginFilter = 'all' | FirstSource;
/** `real` hides the scratch orgs and E2E fixtures; `all` shows the catalogue whole. */
export type KindFilter = 'real' | 'all';

export interface ActivationFilters {
    readonly status: StatusFilter;
    readonly origin: OriginFilter;
    readonly kind: KindFilter;
    readonly search: string;
}

/**
 * Whether a row survives the sandbox filter.
 *
 * Only `isSandbox === true` is hidden. `null` — the catalogue not saying — is
 * kept on purpose: it is not a claim that the tenant is a scratch org, and
 * hiding a real museum because a field was missing is the worse error of the
 * two. An operator who wants everything switches the filter.
 */
function passesKind(museum: ActivationMuseum, kind: KindFilter): boolean {
    return kind === 'all' || museum.isSandbox !== true;
}

/**
 * Status, origin and a search box, all three at once.
 *
 * A museum that never opened the page has no origin, so any origin other than
 * "all" leaves it out — it did not come from an invitation, nor directly.
 *
 * The search is over what the row shows: the name, as a case-insensitive
 * substring. A tenant with no name shows its id instead, so for that row the id
 * is what gets searched.
 */
export function filterMuseums(
    museums: readonly ActivationMuseum[],
    filters: ActivationFilters,
): ActivationMuseum[] {
    const needle = filters.search.trim().toLowerCase();

    return museums.filter(
        (museum) =>
            passesKind(museum, filters.kind) &&
            (filters.status === 'all' || museum.status === filters.status) &&
            (filters.origin === 'all' || museum.firstSource === filters.origin) &&
            (needle.length === 0 || museumLabel(museum).toLowerCase().includes(needle)),
    );
}

/* ------------------------------------------------------------------ order - */

function time(iso: string | null): number {
    const parsed = iso ? Date.parse(iso) : Number.NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Whoever has news first, and simply.
 *
 * - **Museums that opened the page** lead, the most recent activity first.
 *   They are the only rows carrying any information at all.
 * - **Museums that never opened it** close the list, alphabetically.
 *
 * ## Why this is the reverse of what it used to be
 *
 * Until 2026-10-05 the universe was the flag's overrides — four museums, all
 * of them deliberately enrolled — so leading with the ones that had not started
 * was a work list. Then the platform default was switched on and the universe
 * became every museum in the catalogue: that same order now opens the screen on
 * a wall of empty rows and buries the handful that have actually been used.
 *
 * ## And why it does not try to be cleverer
 *
 * No ranking by "needs a nudge": idle-first, fewest-done-first and so on are
 * impossible to predict from the outside — someone looking at the table cannot
 * tell why a row sits where it does. The order is "newest news first", and
 * anything sharper is a filter, which is explicit and reversible.
 *
 * The name breaks every remaining tie, so the order never depends on what order
 * the API happened to answer in.
 */
export function compareMuseums(a: ActivationMuseum, b: ActivationMuseum): number {
    const byName = () => museumLabel(a).localeCompare(museumLabel(b));

    if (a.opened !== b.opened) return a.opened ? -1 : 1;
    if (!a.opened) return byName();

    return time(b.lastOpenedAt) - time(a.lastOpenedAt) || byName();
}

export function sortMuseums(museums: readonly ActivationMuseum[]): ActivationMuseum[] {
    return [...museums].sort(compareMuseums);
}

/* ------------------------------------------------------------------- rows - */

export interface MuseumRowView {
    readonly tenantId: string;
    readonly name: string;
    readonly status: MuseumStatus;
    readonly statusLabel: string;
    /** "as of Sep 25", or "" for a museum that never visited. */
    readonly asOf: string;
    /** `null` when the museum never opened the page: there is nothing to measure. */
    readonly progress: { readonly text: string; readonly percent: number } | null;
    readonly firstOpened: string;
    /** "From invitation" / "Came in directly", or "" when never opened. */
    readonly origin: string;
    readonly lastActivity: string;
    /**
     * Titles of the features found not done, joined by ", ". "—" only when
     * there is nothing at all to say (nothing missing, unchecked or unseen);
     * "" when only the lines under it have something.
     */
    readonly missing: string;
    /** "Couldn't check: A, B", or `null`. */
    readonly couldntCheck: string | null;
    /** "Not seen yet: A, B", or `null`. */
    readonly notSeen: string | null;
}

function titles(features: readonly { title: string }[]): string {
    return features.map((f) => f.title).join(', ');
}

/**
 * The missing column, as the design words it.
 *
 * Empty rather than a dash when the only news is on the lines under it (a
 * feature that could not be checked, or one not seen yet): those lines are
 * then the whole answer, and a dash above them would read as "nothing
 * missing" — which nobody knows.
 */
function missingText(museum: ActivationMuseum): string {
    if (!museum.opened) return DASH;
    if (museum.missing.length > 0) return titles(museum.missing);
    return museum.couldntCheck.length > 0 || museum.notSeen.length > 0 ? '' : DASH;
}

export function toMuseumRow(museum: ActivationMuseum, now: Date = new Date()): MuseumRowView {
    const opened = museum.opened;

    return {
        tenantId: museum.tenantId,
        name: museumLabel(museum),
        status: museum.status,
        statusLabel: STATUS_LABEL[museum.status],
        asOf: opened ? asOfLabel(museum.lastOpenedAt) : '',
        progress: opened
            ? {
                  text: progressText(museum.done, museum.total),
                  percent: museum.total > 0 ? (museum.done / museum.total) * 100 : 0,
              }
            : null,
        firstOpened: opened ? relativeDay(museum.firstOpenedAt, now) : DASH,
        origin: opened && museum.firstSource ? ORIGIN_LABEL[museum.firstSource] : '',
        lastActivity: opened ? relativeDay(museum.lastOpenedAt, now) : DASH,
        missing: missingText(museum),
        couldntCheck:
            opened && museum.couldntCheck.length > 0
                ? `Couldn't check: ${titles(museum.couldntCheck)}`
                : null,
        notSeen:
            opened && museum.notSeen.length > 0 ? `Not seen yet: ${titles(museum.notSeen)}` : null,
    };
}

export function progressText(done: number, total: number): string {
    return `${String(done)} of ${String(total)}`;
}

export function museumCountLabel(count: number): string {
    return count === 1 ? '1 museum' : `${String(count)} museums`;
}
